import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Note {
  id: string;
  message: string;
  staffName?: string;
  resolved: boolean;
  resolvedByName?: string;
  resolvedAt?: string;
  createdAt: string;
}

/**
 * The message board. Things one shift needs to tell the next — a customer
 * who has pre-paid, a machine playing up — kept until someone marks them
 * dealt with.
 */
export default function NotesPanel({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName?: string;
}) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [message, setMessage] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const res = await apiClient.listNotes(branchId, showResolved);
      setNotes(Array.isArray(res.data) ? res.data : []);
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, showResolved]);

  const post = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await apiClient.createNote(branchId, message.trim());
      setMessage('');
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not post that');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (n: Note) => {
    try {
      if (n.resolved) await apiClient.reopenNote(n.id);
      else await apiClient.resolveNote(n.id);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not update that note');
    }
  };

  const open = notes.filter((n) => !n.resolved);

  return (
    <div className="space-y-6">
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Leave a note</h3>
          <p className="text-sm text-gray-500">
            For {branchName || 'this shop'} — everyone on shift here will see it
          </p>
        </div>
        <div className="p-6 flex flex-col sm:flex-row gap-3">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post();
            }}
            rows={2}
            placeholder="e.g. Itumeleng has paid for 10 loaves to collect Sunday"
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
          <button
            onClick={post}
            disabled={busy || !message.trim()}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-lg self-start whitespace-nowrap"
          >
            {busy ? 'Posting…' : 'Post note'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex justify-between items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">
            Notes {open.length > 0 && <span className="text-gray-400">· {open.length} open</span>}
          </h3>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded"
            />
            Show dealt with
          </label>
        </div>

        <div className="divide-y">
          {loading ? (
            <p className="py-10 text-center text-gray-500">Loading…</p>
          ) : notes.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-500">No notes right now.</p>
              <p className="text-sm text-gray-400 mt-1">
                Anything the next shift needs to know goes here.
              </p>
            </div>
          ) : (
            notes.map((n) => (
              <div key={n.id} className={`px-6 py-4 flex items-start gap-4 ${n.resolved ? 'opacity-60' : ''}`}>
                <button
                  onClick={() => toggle(n)}
                  title={n.resolved ? 'Reopen' : 'Mark dealt with'}
                  className={`mt-0.5 w-5 h-5 rounded shrink-0 border-2 ${
                    n.resolved
                      ? 'bg-green-600 border-green-600 text-white text-xs leading-none'
                      : 'border-gray-300 hover:border-amber-500'
                  }`}
                >
                  {n.resolved ? '✓' : ''}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-gray-900 ${n.resolved ? 'line-through text-gray-500' : ''}`}>
                    {n.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {n.staffName || 'Someone'} ·{' '}
                    {new Date(n.createdAt).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                    {n.resolved && n.resolvedByName && ` · dealt with by ${n.resolvedByName}`}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
